// Wire-format types for Notification Policy (PRD 17 알림 정책, FEATURE_049).
//
// The policy is a FIXED catalog of events owned by the backend. The admin screen is
// a READ-ONLY viewer (PRD v1.5/v1.8 + ADM.039): it lists the catalog and displays
// each event's 제목/본문 (raw #변수 text) + a 상태 badge (켜짐/꺼짐). Channel ON/OFF
// and copy are configured in the backend/설정 — there is no write path on this screen.
// The event's Korean label is served by the backend i18n catalog (`labels` namespace,
// key `notificationPolicy.<eventKey>.label`), not authored here.

// Which screen section an event belongs to (PRD 17 §3.7) — 환자 알림 (patient) or
// 의료진 알림 (staff). Served by the backend per event; the wire value is the enum's
// name as JSON serializes it (kr.co.ards.data.shared.NotificationAudience), so 'BOTH'
// is a valid value on the wire even though no event in this catalog returns it today.
export type NotificationAudience = 'PATIENT' | 'STAFF' | 'BOTH';

/** One catalog event's policy as returned by the backend. */
export interface NotificationPolicy {
  // Fixed catalog code, e.g. 'PRO_UNANSWERED'. The Korean label is resolved FE-side
  // via the backend `labels` catalog (notificationPolicy.<eventKey>.label).
  eventKey: string;
  enabled: boolean;
  // Effective push copy (raw template, #변수 unsubstituted). May be null for an event
  // that has no default copy yet.
  title: string | null;
  body: string | null;
  // The item's explanatory note shown beneath its name in the 알림 항목 column (PRD 17 §3.8
  // "표시 내용: 항목명·설명"). Spec metadata served from the backend Korean catalog like the label.
  // Null when the catalog has no note for that event — omit the line, never invent text.
  description: string | null;
  // Read-only policy notes shown beneath the copy: what makes the notification fire, and when it
  // is sent. Served from the backend catalog like the label, so they are never authored here.
  // Null when the catalog carries no note for that event — render a dash, never invent text.
  trigger: string | null;
  timing: string | null;
  // Detection threshold (config-driven, nullable). Consumed by the dispatcher only.
  thresholdN: number | null;
  // The event is shipped but its detection logic is not wired yet (mental-health, TBD).
  // The screen renders a dormant event with a 비활성 badge.
  dormant: boolean;
  // Which screen section this event belongs to — resolved backend-side from the fixed
  // eventKey, so the FE splits sections directly off this field instead of holding its
  // own copy of the eventKey→section mapping.
  audience: NotificationAudience;
  // The 의료진 알림 section's two per-channel status badges (의료진웹 벨 / SMS, PRD 17 §3.7),
  // served independently of each other and of `enabled`: since PRD 17 v1.9 the two individual
  // 09:00 staff events show their bell copy while their SMS is delivered by the single combined
  // STAFF_DIGEST_SMS row, so bell-on/SMS-off and bell-off/SMS-on are both real states.
  // Both null for a 환자 알림 event (patient channel = 앱 푸시 + 벨, no automatic SMS) — that
  // section renders its single push column from `enabled`.
  bellEnabled: boolean | null;
  smsEnabled: boolean | null;
}

// Wire codes the backend returns on the read path (admin-api ErrorCode + CommonErrorCode);
// the FE keys i18n off these. NETWORK is FE-synthesized when the request never reached the
// server. INSUFFICIENT_PERMISSION is surfaced as a Modal (Error Standard §7.1).
export type NotificationSettingsApiErrorCode =
  | 'INSUFFICIENT_PERMISSION'
  | 'NETWORK'
  | 'UNKNOWN';

export class NotificationSettingsApiError extends Error {
  readonly code: NotificationSettingsApiErrorCode;
  readonly fields?: Record<string, string>;
  readonly meta?: Record<string, unknown>;

  constructor(
    code: NotificationSettingsApiErrorCode,
    message: string,
    options?: { fields?: Record<string, string>; meta?: Record<string, unknown> },
  ) {
    super(message);
    this.name = 'NotificationSettingsApiError';
    this.code = code;
    this.fields = options?.fields;
    this.meta = options?.meta;
    Object.setPrototypeOf(this, NotificationSettingsApiError.prototype);
  }
}

// One row of the 「SMS·이메일 발신 전체(참고)」 inventory (PRD 17 §3.8) — SMS/email sends that fall
// OUTSIDE the automatic notification policy. Served Korean-only by the backend i18n catalog; read-only.
export interface SendingReferenceRow {
  sender: string; // 발신 — what is sent
  channel: string; // 채널 — SMS / 이메일 / 앱 내 알림 …
  timing: string; // 시점 · 트리거
  category: string; // 구분 — 시스템 필수 / 의료진 수동 / 자동·본 페이지 …
}

// The inventory, split into the two recipient-scoped tables the screen renders (PRD/prototype model).
export interface SendingReference {
  patientReceives: SendingReferenceRow[];
  staffReceives: SendingReferenceRow[];
}

export interface NotificationSettingsApi {
  // PRD 17 §3.7 — load every event in the catalog. READ+. (No write path — read-only viewer.)
  list(): Promise<NotificationPolicy[]>;
  // PRD 17 §3.8 — the SMS·email sending reference inventory (informational; READ+).
  getSendingReference(): Promise<SendingReference>;
}
